/**
 * DeleteConfirmButton — Shared morph-style delete confirmation.
 *
 * First click: button morphs to "Kyllä, poista" + "Peruuta" appears.
 * Second click: calls onDelete.
 * Optional warning banner text shown when in confirm state.
 *
 * Usage:
 *   <DeleteConfirmButton onDelete={handleDelete} />
 *   <DeleteConfirmButton onDelete={handleDelete} warning="Tämä poistaa myös 3 aliajatusta." />
 */

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Trash2, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const Wrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Btn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: ${props => props.$pad || '8px 14px'};
  font-size: ${props => props.$fontSize || '0.85rem'};
  font-weight: 600;
  font-family: 'Rational', sans-serif;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid;

  background: ${props => props.$confirm ? 'rgba(239, 68, 68, 0.15)' : 'transparent'};
  border-color: ${props => props.$confirm ? '#ef4444' : 'rgba(239, 68, 68, 0.4)'};
  color: ${props => props.$confirm ? '#f87171' : '#f87171'};

  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const CancelBtn = styled.button`
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Rational', sans-serif;
  border-radius: 4px;
  cursor: pointer;
  background: transparent;
  border: 1px solid #444;
  color: #888;
  transition: all 0.15s;

  &:hover {
    border-color: #666;
    color: #ccc;
  }
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 4px;
  font-size: 0.8rem;
  color: #f87171;
  margin-bottom: 8px;

  svg { flex-shrink: 0; }
`;

export default function DeleteConfirmButton({
  onDelete,
  label,
  confirmLabel,
  deletingLabel,
  warning,
  blockedMessage,
  disabled = false,
  deleting = false,
}) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('common.delete');
  const resolvedConfirmLabel = confirmLabel ?? t('common.confirmDelete');
  const resolvedDeletingLabel = deletingLabel ?? t('common.deleting');
  const [confirm, setConfirm] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);

  const handleClick = async () => {
    if (blockedMessage) {
      setShowBlocked(true);
      return;
    }
    if (!confirm) {
      setConfirm(true);
      return;
    }
    await onDelete?.();
  };

  return (
    <div>
      {showBlocked && blockedMessage && (
        <Banner>
          <AlertTriangle size={14} />
          <span>{blockedMessage}</span>
        </Banner>
      )}
      {confirm && warning && (
        <Banner>
          <AlertTriangle size={14} />
          <span>{warning}</span>
        </Banner>
      )}
      <Wrap>
        {confirm && (
          <CancelBtn onClick={() => setConfirm(false)} disabled={deleting}>
            {t('common.cancel')}
          </CancelBtn>
        )}
        <Btn
          $confirm={confirm}
          onClick={handleClick}
          disabled={disabled || deleting}
          style={blockedMessage ? { opacity: 0.5 } : undefined}
        >
          <Trash2 size={14} />
          {deleting ? resolvedDeletingLabel : confirm ? resolvedConfirmLabel : resolvedLabel}
        </Btn>
      </Wrap>
    </div>
  );
}
